import { useState, useEffect, useCallback } from 'react';
import type { CSSProperties } from 'react';
import { Card, Spin, Alert, Button, Typography, Space, Table, Tabs, Tooltip } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { ReloadOutlined, CheckCircleOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import { useAuth } from '../../hooks/useAuth';
import stationsStatusService, {
  type StationStatus,
  type StationBucketFile,
  type StationsStatusResponse,
} from '../../api/stationsStatusService';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import './StationsStatusPage.css';

dayjs.extend(utc);

const { Title, Text } = Typography;

function getFileTableColumns(bucket: string): ColumnsType<StationBucketFile> {
  return [
    {
      title: 'File',
      dataIndex: 'name',
      key: 'name',
      render: (name: string) => <Text code>{name}</Text>,
    },
    {
      title: 'Created (UTC)',
      dataIndex: 'timeCreated',
      key: 'timeCreated',
      width: 170,
      render: (t: string) => dayjs(t).utc().format('YYYY-MM-DD HH:mm:ss'),
    },
    {
      title: 'Path',
      dataIndex: 'path',
      key: 'path',
      ellipsis: true,
      render: (path: string) => (
        <Text code style={{ fontSize: 11 }}>
          gs://{bucket}/{path}
        </Text>
      ),
    },
    {
      title: 'Ingested',
      dataIndex: 'ingested',
      key: 'ingested',
      width: 90,
      render: (_: unknown, record: StationBucketFile) => {
        if (record.ingested === true) {
          return (
            <Tooltip title="station_observations: Row exists">
              <CheckCircleOutlined style={{ color: '#52c41a' }} />
            </Tooltip>
          );
        }
        if (record.ingested === false) {
          return (
            <Tooltip title="station_observations: No row yet">
              <ExclamationCircleOutlined style={{ color: '#faad14' }} />
            </Tooltip>
          );
        }
        return '—';
      },
    },
  ];
}

/** Overall status - one column per station */
interface OverallStatusProps {
  data: StationsStatusResponse | null;
  loading: boolean;
  error: string | null;
  onRefresh: () => void;
}

function OverallStatus({ data, loading, error, onRefresh }: OverallStatusProps) {
  const stations = data?.stations ?? [];
  const hasEvent = data?.hasEvent ?? false;
  const anyDelayed = data?.anyDelayed ?? false;
  const hasError = !!error;

  const columnStyle: CSSProperties = {
    minWidth: 0,
    maxHeight: 'calc(100vh - 220px)',
    overflowY: 'auto',
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  };

  return (
    <Card
      title={
        <Space>
          <span>Status</span>
          <Button
            type="text"
            size="small"
            icon={<ReloadOutlined spin={loading} />}
            onClick={onRefresh}
            disabled={loading}
          >
            Refresh
          </Button>
        </Space>
      }
      style={{
        borderColor: hasError ? '#ff4d4f' : anyDelayed ? '#ff7875' : '#52c41a',
        borderWidth: hasError || anyDelayed ? 2 : 1,
        width: '100%',
      }}
      styles={{
        body: {
          display: stations.length > 0 ? 'grid' : 'block',
          gridTemplateColumns: stations.length > 0 ? `repeat(${Math.min(stations.length, 4)}, 1fr)` : undefined,
          gap: 24,
          maxHeight: 'calc(100vh - 220px)',
          overflow: 'hidden',
        },
      }}
    >
      {loading && !data ? (
        <div style={{ textAlign: 'center', padding: 24 }}>
          <Spin size="large" />
        </div>
      ) : error ? (
        <Alert
          type="error"
          showIcon
          icon={<ExclamationCircleOutlined />}
          title="Error loading status"
          description={error}
        />
      ) : !hasEvent || stations.length === 0 ? (
        <Alert
          type="info"
          title="No active event or no stations"
          description={
            data?.message ?? 'Stations status is available when an event is currently active and has stations configured.'
          }
        />
      ) : (
        stations.map((s) => (
          <div key={s.reference} style={columnStyle}>
            <Text strong style={{ display: 'block', marginBottom: 8, flexShrink: 0 }}>
              Station {s.reference}
            </Text>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                padding: '12px 16px',
                borderRadius: 8,
                background: s.isDelayed ? '#fff2f0' : '#f6ffed',
                border: `1px solid ${s.isDelayed ? '#ffccc7' : '#b7eb8f'}`,
                flexShrink: 0,
              }}
            >
              {s.isDelayed ? (
                <ExclamationCircleOutlined style={{ color: '#cf1322', fontSize: 24 }} />
              ) : (
                <CheckCircleOutlined style={{ color: '#52c41a', fontSize: 24 }} />
              )}
              <div>
                <Text strong={s.isDelayed} type={s.isDelayed ? 'danger' : undefined}>
                  {s.message}
                </Text>
                <div style={{ marginTop: 4 }}>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    Expected: {s.expectedFile}
                  </Text>
                </div>
              </div>
            </div>

            <div style={{ flexShrink: 0 }}>
              <Text strong style={{ display: 'block', marginBottom: 8, marginTop: 16 }}>
                Expected file status
              </Text>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                {s.expectedFileExists ? (
                  <Tooltip title="GCS file: Present in bucket">
                    <CheckCircleOutlined style={{ color: '#52c41a' }} />
                  </Tooltip>
                ) : (
                  <Tooltip title="GCS file: Missing from bucket">
                    <ExclamationCircleOutlined style={{ color: '#cf1322' }} />
                  </Tooltip>
                )}
                <Text code style={{ fontSize: 12 }}>{s.expectedFile}</Text>
                <Text type="secondary">
                  {s.expectedFileExists && s.expectedFileTimeCreated
                    ? `${dayjs(s.expectedFileTimeCreated).utc().format('YYYY-MM-DD HH:mm:ss')} UTC`
                    : 'Missing'}
                </Text>
                {s.expectedFileExists && s.expectedFileIngested === true && (
                  <Tooltip title="station_observations: Row exists">
                    <CheckCircleOutlined style={{ color: '#52c41a', fontSize: 12 }} />
                  </Tooltip>
                )}
                {s.expectedFileExists && s.expectedFileIngested === false && (
                  <Tooltip title="station_observations: No row yet">
                    <ExclamationCircleOutlined style={{ color: '#faad14', fontSize: 12 }} />
                  </Tooltip>
                )}
              </div>
            </div>
          </div>
        ))
      )}
    </Card>
  );
}

/** Per-station tab - status banner + latest files table */
interface StationTabProps {
  station: StationStatus | null;
  loading: boolean;
  error: string | null;
  onRefresh: () => void;
}

function StationTab({ station, loading, error, onRefresh }: StationTabProps) {
  const isDelayed = station?.isDelayed ?? false;
  const hasError = !!error;

  return (
    <Card
      title={
        <Space>
          <span>{station ? `Station ${station.reference}` : 'Station'}</span>
          <Button
            type="text"
            size="small"
            icon={<ReloadOutlined spin={loading} />}
            onClick={onRefresh}
            disabled={loading}
          >
            Refresh
          </Button>
        </Space>
      }
      style={{
        flex: 1,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        borderColor: hasError ? '#ff4d4f' : isDelayed ? '#ff7875' : '#52c41a',
        borderWidth: hasError || isDelayed ? 2 : 1,
      }}
      styles={{
        body: {
          flex: 1,
          minHeight: 0,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          padding: '16px 24px',
        },
      }}
    >
      {loading && !station ? (
        <div style={{ textAlign: 'center', padding: 24 }}>
          <Spin size="large" />
        </div>
      ) : error ? (
        <Alert
          type="error"
          showIcon
          icon={<ExclamationCircleOutlined />}
          title="Error loading status"
          description={error}
        />
      ) : station ? (
        <Space vertical size="middle" style={{ width: '100%', flex: 1, minHeight: 0 }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              padding: '12px 16px',
              borderRadius: 8,
              background: isDelayed ? '#fff2f0' : '#f6ffed',
              border: `1px solid ${isDelayed ? '#ffccc7' : '#b7eb8f'}`,
              flexShrink: 0,
            }}
          >
            {isDelayed ? (
              <ExclamationCircleOutlined style={{ color: '#cf1322', fontSize: 24 }} />
            ) : (
              <CheckCircleOutlined style={{ color: '#52c41a', fontSize: 24 }} />
            )}
            <div>
              <Text strong={isDelayed} type={isDelayed ? 'danger' : undefined}>
                {station.message}
              </Text>
              <div style={{ marginTop: 4 }}>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Expected: {station.expectedFile}
                </Text>
              </div>
            </div>
          </div>

          <div style={{ flexShrink: 0 }}>
            <Text strong style={{ display: 'block', marginBottom: 8 }}>
              Expected file status
            </Text>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              {station.expectedFileExists ? (
                <Tooltip title="GCS file: Present in bucket">
                  <CheckCircleOutlined style={{ color: '#52c41a' }} />
                </Tooltip>
              ) : (
                <Tooltip title="GCS file: Missing from bucket">
                  <ExclamationCircleOutlined style={{ color: '#cf1322' }} />
                </Tooltip>
              )}
              <Text code style={{ fontSize: 12 }}>{station.expectedFile}</Text>
              <Text type="secondary">
                {station.expectedFileExists && station.expectedFileTimeCreated
                  ? `${dayjs(station.expectedFileTimeCreated).utc().format('YYYY-MM-DD HH:mm:ss')} UTC`
                  : 'Missing'}
              </Text>
              {station.expectedFileExists && station.expectedFileIngested === true && (
                <Tooltip title="station_observations: Row exists">
                  <CheckCircleOutlined style={{ color: '#52c41a', fontSize: 12 }} />
                </Tooltip>
              )}
              {station.expectedFileExists && station.expectedFileIngested === false && (
                <Tooltip title="station_observations: No row yet">
                  <ExclamationCircleOutlined style={{ color: '#faad14', fontSize: 12 }} />
                </Tooltip>
              )}
            </div>
          </div>

          {station.latestFiles.length > 0 && (
            <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
              <Text strong style={{ marginBottom: 8 }}>
                Latest {station.latestFiles.length} files (current hour)
              </Text>
              <Table
                size="small"
                dataSource={station.latestFiles}
                rowKey={(r) => r.path}
                columns={getFileTableColumns(station.bucket)}
                pagination={false}
                scroll={{ y: 'calc(100vh - 650px)' }}
              />
            </div>
          )}
        </Space>
      ) : (
        <Alert type="info" title="No data available" />
      )}
    </Card>
  );
}

export const StationsStatusPage = () => {
  const { authState } = useAuth();
  const [data, setData] = useState<StationsStatusResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchStatus = useCallback(async () => {
    const token = authState.token;
    if (!token) {
      setError('Not authenticated');
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const result = await stationsStatusService.getStationsStatus(token);
      setData(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load status');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [authState.token]);

  useEffect(() => {
    fetchStatus();
    const interval = setInterval(fetchStatus, 60 * 1000);
    return () => clearInterval(interval);
  }, [fetchStatus]);

  const stations = data?.stations ?? [];
  const tabItems = [
    {
      key: 'overall',
      label: 'Overall Status',
      children: (
        <OverallStatus data={data} loading={loading} error={error} onRefresh={fetchStatus} />
      ),
    },
    ...stations.map((s) => ({
      key: `station-${s.reference}`,
      label: `Station ${s.reference}`,
      children: (
        <div style={{ height: '100%', minHeight: 0, display: 'flex', flexDirection: 'column' }}>
          <StationTab
            station={s}
            loading={loading}
            error={error}
            onRefresh={fetchStatus}
          />
        </div>
      ),
    })),
  ];

  return (
    <div
      style={{
        padding: 24,
        height: 'calc(100vh - 64px)',
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
        boxSizing: 'border-box',
      }}
    >
      <Title level={3} style={{ marginBottom: 8, flexShrink: 0 }}>
        Stations Status
      </Title>
      <p style={{ marginBottom: 16, color: 'rgba(0,0,0,0.65)', flexShrink: 0 }}>
        Monitor station CSV files being pushed to GCS. Files are expected to arrive within the
        minute of the file name.
      </p>

      <Tabs
        defaultActiveKey="overall"
        className="stations-status-tabs"
        style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}
        items={tabItems}
      />
    </div>
  );
};
